'use client';

import type { ReactNode} from 'react';
import type { ClientResponseDto } from '@/apis/backend/generated/models/clientResponseDto';
import type { OrganizationResponseDto } from '@/apis/backend/generated/models/organizationResponseDto';

import { paths } from 'src/routes/paths';
import { useNavigate } from '@tanstack/react-router';
import { useTranslation } from 'react-i18next';
import React, { useRef, useState, useEffect, useContext, useCallback, createContext } from 'react';
import { useOrganizationsControllerFindAll } from '@/apis/backend/generated/organizations/organizations';
import { OrganizationResponseDtoRole } from '@/apis/backend/generated/models/organizationResponseDtoRole';
import { useOrganizationClientsControllerFindAll } from '@/apis/backend/generated/organization-clients/organization-clients';

import { useAuth } from 'src/lib/store/auth-store';
import { STORAGE_KEY_ACTIVE_ORG, STORAGE_KEY_ACTIVE_CLIENT } from 'src/lib/active-tenant';

// Use generated types
export type Organization = OrganizationResponseDto & { userRole?: OrganizationResponseDtoRole };
export type Client = ClientResponseDto;
export type OrganizationRole = OrganizationResponseDtoRole;

interface OrganizationContextValue {
  // State
  organizations: Organization[];
  activeOrganization: Organization | null;
  activeClient: Client | null;
  accessibleClients: Client[];
  userRole: OrganizationRole | null;
  hasAccessToAllClients: boolean;

  // Loading states
  loading: boolean;
  loadingClients: boolean;

  // Error state
  error: string | null;

  // Methods
  switchOrganization: (organizationId: string) => Promise<void>;
  switchClient: (clientId: string) => void;
  refetch: () => Promise<void>;

  /**
   * The organization an explicit switch is heading for, held until the URL
   * names it. Read by useSyncTenantFromParams so the route we are leaving
   * cannot sync its now-stale param back over the switch.
   */
  orgSwitchIntentRef: { current: string | null };

  // Helper methods
  isOwner: () => boolean;
  isEditor: () => boolean;
  canManageClients: () => boolean;
  canManageTeam: () => boolean;
}

const OrganizationContext = createContext<OrganizationContextValue | undefined>(undefined);

const MAX_RETRIES = 3;
const RETRY_DELAYS = [500, 1000, 2000]; // ms

export const OrganizationProvider = ({ children }: { children: ReactNode }) => {
  const { t } = useTranslation('common');
  const { isAuthenticated } = useAuth();

  const [activeOrganization, setActiveOrganization] = useState<Organization | null>(null);
  const [activeClient, setActiveClient] = useState<Client | null>(null);
  const [userRole, setUserRole] = useState<OrganizationRole | null>(null);
  const [hasAccessToAllClients, setHasAccessToAllClients] = useState<boolean>(false);
  const [error, setError] = useState<string | null>(null);
  const retryCountRef = useRef(0);
  const retryTimeoutRef = useRef<NodeJS.Timeout | null>(null);
  const orgSwitchIntentRef = useRef<string | null>(null);


  // Fetch user's organizations using generated hook
  const {
    data: orgsData,
    isLoading: loadingOrgs,
    error: orgsError,
    refetch: refetchOrgs,
  } = useOrganizationsControllerFindAll({
    query: {
      enabled: isAuthenticated,
    },
  });

  // Fetch clients for active organization using generated hook
  // Note: We fetch clients for ALL org types (not just agency) because:
  // - Brand orgs have a single default client that needs to be set as activeClient
  // - API queries require clientId even for brand orgs
  // - The client switcher UI is separately hidden for brand orgs in nav-bottom-section.tsx
  const {
    data: clientsData,
    isLoading: loadingClients,
    error: clientsError,
    refetch: refetchClients,
  } = useOrganizationClientsControllerFindAll(activeOrganization?.id || '', {
    query: {
      enabled: !!activeOrganization?.id,
    },
  });


  // Process organizations data when it changes, with retry logic for empty arrays
  useEffect(() => {
    // Clear any pending retry timeouts
    if (retryTimeoutRef.current) {
      clearTimeout(retryTimeoutRef.current);
      retryTimeoutRef.current = null;
    }

    // If no data yet, wait for the query
    if (!orgsData) return;

    // If empty array and user is authenticated, retry
    if (orgsData.length === 0 && isAuthenticated) {
      if (retryCountRef.current < MAX_RETRIES) {
        const delay = RETRY_DELAYS[retryCountRef.current] || 2000;
        console.log(`[OrganizationContext] Organizations empty, retrying in ${delay}ms (attempt ${retryCountRef.current + 1}/${MAX_RETRIES})`);

        retryTimeoutRef.current = setTimeout(() => {
          retryCountRef.current += 1;
          refetchOrgs();
        }, delay);
      } else {
        console.error('[OrganizationContext] Max retries reached, no organizations found');
        setError(t('organizationContext.error.noOrgsFound', 'No organizations found after multiple attempts'));
      }
      return;
    }

    // Reset retry count on success
    if (orgsData.length > 0) {
      retryCountRef.current = 0;
    }

    // If still no orgs, just return
    if (!orgsData.length) return;

    const processOrganizations = async () => {
      try {
        // Get stored or first org
        const storedOrgId = localStorage.getItem(STORAGE_KEY_ACTIVE_ORG);
        const selectedOrg = storedOrgId
          ? orgsData.find((org) => org.id === storedOrgId) || orgsData[0]
          : orgsData[0];

        if (selectedOrg) {
          setActiveOrganization({
            ...selectedOrg,
            userRole: selectedOrg.role,
          });
          setUserRole(selectedOrg.role);
          setHasAccessToAllClients(selectedOrg.hasAccessToAllClients || false);
          localStorage.setItem(STORAGE_KEY_ACTIVE_ORG, selectedOrg.id);
        }
      } catch (err: any) {
        console.error('[OrganizationContext] Error processing organizations:', err);
        setError(err.message || t('organizationContext.error.failedToProcessOrgs', 'Failed to process organizations'));
      }
    };

    processOrganizations();
  }, [orgsData, isAuthenticated, refetchOrgs]);

  // Process clients data when it changes
  useEffect(() => {
    if (!clientsData || !clientsData.length || !activeOrganization) return;

    const storedClientId = localStorage.getItem(STORAGE_KEY_ACTIVE_CLIENT);
    const selectedClient = storedClientId
      ? clientsData.find((client) => client.id === storedClientId) || clientsData[0]
      : clientsData[0];

    if (selectedClient) {
      setActiveClient(selectedClient);
      localStorage.setItem(STORAGE_KEY_ACTIVE_CLIENT, selectedClient.id);
    }
  }, [clientsData, activeOrganization]);

  // Extract organizations list from data (now properly typed)
  const organizations = React.useMemo(() => {
    if (!orgsData) return [];
    return orgsData.map((org) => ({ ...org, userRole: org.role }));
  }, [orgsData]);

  // Extract clients list from data (now properly typed)
  const accessibleClients = React.useMemo(() => clientsData || [], [clientsData]);

  // Switch to a different organization
  const switchOrganization = useCallback(
    async (organizationId: string) => {
      const org = organizations.find((o) => o.id === organizationId);
      if (!org) {
        console.error('[OrganizationContext] Organization not found:', organizationId);
        return;
      }

      // Record the target before touching state. Switching navigates away, but
      // the page we are leaving stays mounted and rendering until it does, and
      // its useSyncTenantFromParams would otherwise read the old orgId out of
      // the URL and call straight back into here to undo this.
      orgSwitchIntentRef.current = organizationId;

      setActiveOrganization(org);
      setUserRole(org.userRole || null);

      // Store in localStorage
      if (typeof window !== 'undefined') {
        localStorage.setItem(STORAGE_KEY_ACTIVE_ORG, org.id);
      }

      // Clear active client when switching orgs
      setActiveClient(null);
      if (typeof window !== 'undefined') {
        localStorage.removeItem(STORAGE_KEY_ACTIVE_CLIENT);
      }

      // React Query will automatically fetch clients for the new org
    },
    [organizations]
  );

  // Switch to a different client
  const switchClient = useCallback(
    (clientId: string) => {
      const client = accessibleClients.find((c) => c.id === clientId);
      if (!client) {
        console.error('[OrganizationContext] Client not found:', clientId);
        return;
      }

      setActiveClient(client);

      // Store in localStorage
      if (typeof window !== 'undefined') {
        localStorage.setItem(STORAGE_KEY_ACTIVE_CLIENT, client.id);
      }
    },
    [accessibleClients]
  );

  // Refetch all data
  const refetch = useCallback(async () => {
    await Promise.all([
      refetchOrgs(),
      refetchClients(),
    ]);
  }, [refetchOrgs, refetchClients]);

  // Helper: Check if user is owner
  const isOwner = useCallback(() => userRole === OrganizationResponseDtoRole.owner, [userRole]);

  // Helper: Check if user is editor (or owner)
  const isEditor = useCallback(() =>
    userRole === OrganizationResponseDtoRole.editor || userRole === OrganizationResponseDtoRole.owner,
    [userRole]
  );

  // Helper: Check if user can manage clients (owner only, plan limits handle quotas)
  const canManageClients = useCallback(() => userRole === OrganizationResponseDtoRole.owner, [userRole]);

  // Helper: Check if user can manage team (owner only)
  const canManageTeam = useCallback(() => userRole === OrganizationResponseDtoRole.owner, [userRole]);

  // Clear state when user logs out
  useEffect(() => {
    if (!isAuthenticated) {
      setActiveOrganization(null);
      setActiveClient(null);
      setUserRole(null);
      setHasAccessToAllClients(false);
      retryCountRef.current = 0;

      // Clear any pending retry timeouts
      if (retryTimeoutRef.current) {
        clearTimeout(retryTimeoutRef.current);
        retryTimeoutRef.current = null;
      }

    }
  }, [isAuthenticated]);

  // Cleanup retry timeout on unmount
  useEffect(() => () => {
      if (retryTimeoutRef.current) {
        clearTimeout(retryTimeoutRef.current);
      }
    }, []);

  // Set combined error state
  useEffect(() => {
    if (orgsError) {
      setError(t('organizationContext.error.failedToLoadOrgs', 'Failed to load organizations'));
    } else if (clientsError) {
      setError(t('organizationContext.error.failedToLoadClients', 'Failed to load clients'));
    } else {
      setError(null);
    }
  }, [orgsError, clientsError]);

  const value: OrganizationContextValue = {
    organizations,
    activeOrganization,
    activeClient,
    accessibleClients,
    userRole,
    hasAccessToAllClients,
    loading: loadingOrgs,
    loadingClients,
    error,
    switchOrganization,
    switchClient,
    orgSwitchIntentRef,
    refetch,
    isOwner,
    isEditor,
    canManageClients,
    canManageTeam,
  };

  return <OrganizationContext.Provider value={value}>{children}</OrganizationContext.Provider>;
};

// Custom hook to use the organization context
export const useOrganization = () => {
  const context = useContext(OrganizationContext);
  if (context === undefined) {
    throw new Error('useOrganization must be used within an OrganizationProvider');
  }
  return context;
};

/**
 * Sync the active org/client FROM url params (the source of truth for tenancy).
 * Used by the tenant layout routes (`/dashboard/o/$orgId/c/$clientId/...`).
 *
 * Each browser tab derives its tenant from its own URL, so tabs are isolated:
 * switching client in one tab never silently changes another. localStorage is
 * still written by switchOrganization/switchClient as a "last used" default for
 * the bare /dashboard entry point, which is harmless because every real page now
 * re-derives its tenant from the URL.
 *
 * The two effects run independently and re-run as data loads, which naturally
 * orders org-before-client: after an org switch the new org's clients load,
 * accessibleClients changes, and the client effect fires. Each switch is guarded
 * on the target existing in the loaded list so we never log the not-found error
 * during the load gap (or for an org/client the user can't access).
 */
export const useSyncTenantFromParams = (orgId?: string, clientId?: string) => {
  const {
    organizations,
    accessibleClients,
    activeOrganization,
    activeClient,
    switchOrganization,
    switchClient,
    orgSwitchIntentRef,
    loading,
    loadingClients,
  } = useOrganization();
  const navigate = useNavigate();

  // An explicit org switch updates context first and navigates second, so until
  // the navigation lands the page being left is still mounted with the old
  // orgId. Syncing from it would call switchOrganization back to the org we
  // just left, and because that writes localStorage, the resolver the switch
  // navigates to reads the old org back out and the switch looks like it did
  // nothing. Stand down until the URL names the org the switch is heading for.
  // Only the org effect needs this: accessibleClients already belongs to the
  // new org, so the client effect can never match the old client param.
  const pendingOrgSwitch =
    !!orgId && !!orgSwitchIntentRef.current && orgSwitchIntentRef.current !== orgId;

  // Once the URL names the target the switch is done, so a later URL-driven org
  // change (a link into another org, the back button) syncs normally again.
  useEffect(() => {
    if (orgId && orgSwitchIntentRef.current === orgId) {
      orgSwitchIntentRef.current = null;
    }
  }, [orgId, orgSwitchIntentRef]);

  useEffect(() => {
    if (
      orgId &&
      !pendingOrgSwitch &&
      activeOrganization?.id !== orgId &&
      organizations.some((o) => o.id === orgId)
    ) {
      switchOrganization(orgId);
    }
  }, [orgId, pendingOrgSwitch, activeOrganization?.id, organizations, switchOrganization]);

  useEffect(() => {
    if (
      clientId &&
      activeClient?.id !== clientId &&
      accessibleClients.some((c) => c.id === clientId)
    ) {
      switchClient(clientId);
    }
  }, [clientId, activeClient?.id, accessibleClients, switchClient]);

  // Guard against a URL pointing at an org/client the user can't access (a
  // shared link to another account's tenant, a deleted client, a typo'd id).
  // The switch effects above no-op for inaccessible ids, which would otherwise
  // strand the user on a broken page with the wrong/empty tenancy. Send them to
  // bare /dashboard, which resolves their real default tenant and forwards on.
  // Each check waits for its data to load so we never bounce mid-fetch.
  const orgInaccessible =
    !!orgId &&
    !loading &&
    organizations.length > 0 &&
    !organizations.some((o) => o.id === orgId);
  // Only validate the client once the active org has settled to the URL org,
  // since accessibleClients is scoped to the active org.
  const clientInaccessible =
    !!clientId &&
    activeOrganization?.id === orgId &&
    !loadingClients &&
    accessibleClients.length > 0 &&
    !accessibleClients.some((c) => c.id === clientId);
  const inaccessibleTenant = orgInaccessible || clientInaccessible;
  useEffect(() => {
    if (inaccessibleTenant) {
      navigate({ to: paths.dashboard.root, replace: true });
    }
  }, [inaccessibleTenant, navigate]);
};

// Optional: Hook to get required org and client (throws if not available)
export const useRequiredOrgAndClient = () => {
  const context = useOrganization();

  if (!context.activeOrganization || !context.activeClient) {
    throw new Error('Organization and Client must be selected');
  }

  return {
    ...context,
    organization: context.activeOrganization,
    client: context.activeClient,
  };
};
